// Relevance ranking for client-side admin search inputs.
//
// Operator-reported bug (verbatim): "the search function throughout the website
// is functioning eratic, when i search for say 'Dorrel' it'll show all the users
// that have been added to the system with Dorrel as their reference first... and
// at the bottom it'll show his name". The legacy admin search filters used a flat
// OR-of-includes() across multiple fields with no relevance scoring; output order
// was therefore the natural order of the source list (typically id ASC), so any
// record whose own identity field happened to contain the substring appeared
// above the user whose primary identity field matched exactly.
//
// scoreRelevance returns a single integer score for a search term against a set
// of "primary" identity fields and "peripheral" fields:
//   Tier 1 (1000): exact equality (case-insensitive, trimmed) on any primary field
//   Tier 2 (500):  prefix match on any primary field
//   Tier 3 (200):  substring match on any primary field
//   Tier 4 (50):   substring match on any peripheral field
// A record with score === 0 is considered a non-match and should be excluded.
//
// Inclusion semantics: every record that would have matched a flat OR-of-includes
// across (primary ∪ peripheral) under the legacy logic still produces a positive
// score here, so callers that previously included a record continue to include it.

export interface RelevanceFields {
  /** Primary identity fields: name, email, username, id, kyc_full_name, etc. */
  primary?: Array<string | number | null | undefined>;
  /** Peripheral fields: phone, secondary refs. Substring-only, lowest tier. */
  peripheral?: Array<string | number | null | undefined>;
}

const norm = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined) return '';
  return String(v).toLowerCase().trim();
};

export function scoreRelevance(term: string, fields: RelevanceFields): number {
  const t = (term || '').toLowerCase().trim();
  if (!t) return 0;

  const primary = (fields.primary || []).map(norm).filter(Boolean);
  const peripheral = (fields.peripheral || []).map(norm).filter(Boolean);

  // Tier 1: exact equality on any primary field.
  for (const v of primary) {
    if (v === t) return 1000;
  }
  // Tier 2: prefix match on any primary field.
  for (const v of primary) {
    if (v.startsWith(t)) return 500;
  }
  // Tier 3: substring match on any primary field.
  for (const v of primary) {
    if (v.includes(t)) return 200;
  }
  // Tier 4: substring match on any peripheral field.
  for (const v of peripheral) {
    if (v.includes(t)) return 50;
  }
  return 0;
}

/**
 * filterAndRank: filter `items` to those that match `term` and sort by relevance.
 * If `term` is empty/whitespace, returns `items` unchanged (no filter, no sort).
 * Items with equal relevance scores are ordered by `tiebreaker` if provided, else
 * preserve their input order (stable sort is assumed).
 */
export function filterAndRank<T>(
  items: T[],
  term: string,
  getFields: (item: T) => RelevanceFields,
  tiebreaker?: (a: T, b: T) => number,
): T[] {
  const t = (term || '').trim();
  if (!t) return items;

  const scored = items
    .map((item, index) => ({ item, index, score: scoreRelevance(t, getFields(item)) }))
    .filter((s) => s.score > 0);

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (tiebreaker) {
      const tb = tiebreaker(a.item, b.item);
      if (tb !== 0) return tb;
    }
    return a.index - b.index;
  });

  return scored.map((s) => s.item);
}
